import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprite, SparkleBurst, PromptCapsule, StarPips, CheckIcon } from "./fx";

// FeelingsMatch — the fountain shows one feeling at a time; tap the big face
// that matches. No dragging. Faithful port of iOS CityHelperFeelingsView.

const FeelingsMatch = ({ payload, sounds, onDone }) => {
  const feelings = payload.feelings;
  const doneRef = useRef(false);
  const [order] = useState(() => {
    const idx = feelings.map((_, i) => i);
    for (let i = idx.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [idx[i], idx[j]] = [idx[j], idx[i]];
    }
    return idx;
  });
  const [round, setRound] = useState(0);
  const [matched, setMatched] = useState(() => new Set());
  const [wiggle, setWiggle] = useState(null);
  const [sparkle, setSparkle] = useState(0);
  const [showTarget, setShowTarget] = useState(false);

  const isDone = round >= order.length;
  const current = useMemo(
    () => (!isDone && order[round] != null ? feelings[order[round]] : null),
    [feelings, isDone, order, round]
  );

  const reveal = useCallback(
    (card) => {
      if (!card) return;
      setShowTarget(false);
      window.setTimeout(() => setShowTarget(true), 40);
      sounds.speak(`Who is feeling ${card.name.toLowerCase()}?`);
    },
    [sounds]
  );

  useEffect(() => {
    const id = window.setTimeout(() => reveal(current), 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  const tap = useCallback(
    (card) => {
      const target = current;
      if (!target || matched.has(card.id)) return;
      if (card.id === target.id) {
        sounds.playPop(0.7 + round * 0.12);
        sounds.playSparkle();
        setMatched((prev) => new Set(prev).add(card.id));
        setSparkle((s) => s + 1);
        const nextRound = round + 1;
        setRound(nextRound);
        if (nextRound >= order.length && !doneRef.current) {
          doneRef.current = true;
          window.setTimeout(() => onDone(), 700);
        }
      } else {
        sounds.playThud();
        setWiggle(`${card.id}-${Date.now()}`);
        sounds.speak(`That's the ${card.name.toLowerCase()} face. Who looks ${target.name.toLowerCase()}?`);
      }
    },
    [current, matched, onDone, order.length, round, sounds]
  );

  return (
    <div className="ch-activity-body ch-feelings">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <div className="ch-feelings-fountain">
        <span
          className="ch-fountain-pool"
          style={{ "--ch-feel-tint": current?.tint || "#ff7a5c" }}
        />
        {current && showTarget && (
          <span
            className="ch-fountain-ring"
            style={{ borderColor: current.tint }}
            aria-hidden="true"
          />
        )}
        <AnimatePresence mode="wait">
          {current && showTarget && (
            <motion.div
              key={current.id}
              className="ch-fountain-show"
              initial={{ scale: 0.3, opacity: 0 }}
              animate={{ scale: [1, 1.06, 1], opacity: 1 }}
              exit={{ scale: 0.3, opacity: 0 }}
              transition={{ scale: { duration: 1.2, repeat: Infinity }, opacity: { duration: 0.4 } }}
            >
              <Sprite name={current.poolAsset} className="ch-fountain-icon" />
              <span className="ch-fountain-name" style={{ color: current.tint }}>
                {current.name.toUpperCase()}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
        <SparkleBurst trigger={sparkle} reach={120} style={{ left: "50%", top: "50%" }} />
        <div className="ch-feelings-dots">
          <StarPips total={order.length} done={round} />
        </div>
      </div>

      <div className="ch-feelings-cards">
        {feelings.map((card, idx) => {
          const isMatched = matched.has(card.id);
          return (
            <motion.button
              key={card.id}
              type="button"
              className={`ch-feel-card ${isMatched ? "is-matched" : ""}`}
              onClick={() => tap(card)}
              disabled={isMatched || isDone}
              initial={{ opacity: 0, y: 24, scale: 0.9 }}
              animate={
                wiggle && wiggle.startsWith(card.id)
                  ? { opacity: 1, y: 0, scale: 1, x: [0, -8, 8, -5, 5, 0] }
                  : { opacity: 1, y: 0, scale: 1 }
              }
              transition={{ duration: 0.4, delay: wiggle || matched.size ? 0 : 0.2 + idx * 0.1 }}
            >
              {isMatched && (
                <motion.span
                  className="ch-feel-medal"
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 320, damping: 14 }}
                >
                  <CheckIcon size={15} />
                </motion.span>
              )}
              <span className="ch-feel-face"><Sprite name={card.faceAsset} /></span>
              <span className="ch-feel-label">{card.name}</span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default FeelingsMatch;
